import React from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import CreatePageSplitLayout from '@gik/create/components/CreatePageSplitLayout/CreatePageSplitLayout';
import { translationKeys } from '@gik/create/i18n/en';
import i18n from '@gik/i18n';
import { useCreateFlowStore } from '@gik/create';
import { auth, LoginForm, SignupForm } from '@gik/auth';
import ExternalLoginForm, { ExternalLoginFormFilter } from '@gik/auth/components/ExternalLogin/ExternalLoginForm';
import { CreatePageContext } from '@gik/create/components/CreatePage/CreatePage';
import { Button, Form, FormButtons, FormField, FormSchemaEntry, HTMLParser, LoadingSpinner, UI } from '@gik/ui';
import { Case, Default, Switch } from '@gik/core/utils/SwitchCase';
import { performLogin } from '@gik/auth/utils/auth';
import { IUserLoginFormResponse, IUserSignUpResponse } from '@gik/api/users/auth';
import { CreateAnalyticsEvents } from '@gik/create/hooks/useCreateAnalytics';
import coreTranslationKeys from '@gik/i18n/en/core';
import { ICreateAboutFormValues } from '@gik/create/components/AboutStep/AboutStep';
import { AboutStepInputNames } from '@gik/create/enums/AboutStepInputNames';
import { EmailTakenStatus } from '@gik/create/enums/EmailTakenStatus';

export interface IFinishStepProps {
  onSubmit?: (values: Pick<ICreateAboutFormValues, AboutStepInputNames.Email>) => void;
  onSuccess?: () => void;
}

const blockName = 'finish-step';

export default function FinishStep({ onSubmit, onSuccess }: IFinishStepProps) {
  const bem = bemBlock(blockName);
  const emailTakenStatus = useCreateFlowStore(state => state.emailTakenStatus);
  const buttonsRef = React.useRef();
  const [changeEmailFormValues, setChangeEmailFormValues] = React.useState<{
    [AboutStepInputNames.Email]: string;
    confirmEmail: string;
  }>();

  React.useEffect(() => {
    if (changeEmailFormValues) {
      onSubmit?.({ [AboutStepInputNames.Email]: changeEmailFormValues[AboutStepInputNames.Email] });
    }
  }, [changeEmailFormValues]);

  const url = new URL(window.location.toString());
  url.searchParams.set('finish', 'true');
  const externalLoginReturnUrl = url.toString();

  async function openChangeEmailModal() {
    const schema: FormSchemaEntry[] = [
      {
        type: 'email',
        placeholder: 'Email',
        name: AboutStepInputNames.Email,
      },
    ];

    const formId = bem('change-email');

    // TODO we should abstract this into a UI.formDialog or something
    await UI.confirm(
      <div className={bem('change-email-content')}>
        <div className={bem('change-email-description')}>{i18n.t(translationKeys.ChangeEmailDescription)}</div>
        <Form
          schema={schema}
          trackingId={CreateAnalyticsEvents.CREATE_STARTED}
          onSubmit={setChangeEmailFormValues}
          id={formId}
          render={form => {
            return (
              <>
                {schema.map((field, i) => (
                  <FormField key={i} name={field.name} />
                ))}

                <FormButtons
                  buttons={({ submitting, valid }) => (
                    <Button
                      type="submit"
                      onClick={() => UI.closeDialog()}
                      form={formId}
                      disabled={submitting || !valid}
                    >
                      {i18n.t(coreTranslationKeys.core.update)}
                    </Button>
                  )}
                  buttonsPortal={() => buttonsRef.current}
                  form={form}
                  formId={formId}
                />
              </>
            );
          }}
        />
      </div>,
      {
        title: i18n.t(translationKeys.ChangeEmail),
        buttons: () => {
          return (
            <div ref={buttonsRef} className={bem('change-email-buttons')}>
              <Button variant="default" onClick={() => UI.closeDialog()}>
                {i18n.t(coreTranslationKeys.core.cancel)}
              </Button>
            </div>
          );
        },
      }
    );
  }

  // TODO: remove success and all translations etc.
  // FIXME: <Default> is showing if network request fails
  return (
    <Switch value={emailTakenStatus}>
      <Case value={EmailTakenStatus.FREE}>
        <SignUpStep
          onSuccess={onSuccess}
          openChangeEmailModal={openChangeEmailModal}
          externalLoginReturnUrl={externalLoginReturnUrl}
        />
      </Case>
      <Case value={EmailTakenStatus.GIK}>
        <LoginStep onSuccess={onSuccess} openChangeEmailModal={openChangeEmailModal} />
      </Case>
      <Case value={EmailTakenStatus.FACEBOOK}>
        <FacebookLogin openChangeEmailModal={openChangeEmailModal} externalLoginReturnUrl={externalLoginReturnUrl} />
      </Case>
      <Case value={EmailTakenStatus.GOOGLE}>
        <GoogleLogin openChangeEmailModal={openChangeEmailModal} externalLoginReturnUrl={externalLoginReturnUrl} />
      </Case>
      <Default>
        <div className={bem('loading')}>
          <LoadingSpinner center />
        </div>
      </Default>
    </Switch>
  );
}

function SignUpStep({ onSuccess, openChangeEmailModal, externalLoginReturnUrl }) {
  const bem = bemBlock(blockName);
  const formValues = useCreateFlowStore(state => state.formValues);
  const setNewUserLoginProvider = useCreateFlowStore(state => state.setNewUserLoginProvider);
  const setFinishButtonSubmitting = useCreateFlowStore(state => state.setFinishButtonSubmitting);
  const { finishFormRef } = React.useContext(CreatePageContext);
  const setNextDisabled = useCreateFlowStore(state => state.setNextDisabled);

  function handleSuccess(response: IUserSignUpResponse) {
    setNewUserLoginProvider('email');
    performLogin(response.id, false);
    onSuccess();
  }

  function handleChange() {
    setNextDisabled(finishFormRef.current.api.current.getState().invalid);
  }

  return (
    <CreatePageSplitLayout className={bem(null, [{ signup: true }])}>
      <main>
        <div className={bem('signup-title')}>{i18n.t(translationKeys.SignUpStepTitle)}</div>
        <div className={bem('signup-description')}>
          <HTMLParser
            rawHtml={i18n.t(translationKeys.SignUpStepDescription, { emailAddress: formValues.emailAddress })}
          />
        </div>
        <Button onClick={openChangeEmailModal} variant={'primary-link'}>
          {i18n.t(translationKeys.SwitchAddress)}
        </Button>

        <ExternalLoginForm returnUrl={externalLoginReturnUrl} appendProviderToReturnUrl signup />
      </main>
      <aside>
        <div className={bem('signup-form-wrapper')}>
          <SignupForm
            onSuccess={handleSuccess}
            onAttemptStart={() => setFinishButtonSubmitting(true)}
            onAttemptEnd={() => setFinishButtonSubmitting(false)}
            onChange={handleChange}
            ref={finishFormRef}
            initialValues={{ email: formValues.emailAddress }}
            trackingId={CreateAnalyticsEvents.CREATE_STARTED}
            hideEmailField
            hideButtons
          />
        </div>
      </aside>
    </CreatePageSplitLayout>
  );
}

function LoginStep({ onSuccess, openChangeEmailModal }) {
  const bem = bemBlock(blockName);
  const formValues = useCreateFlowStore(state => state.formValues);
  const setFinishButtonSubmitting = useCreateFlowStore(state => state.setFinishButtonSubmitting);
  const setNextDisabled = useCreateFlowStore(state => state.setNextDisabled);
  const { finishFormRef } = React.useContext(CreatePageContext);

  function handleSuccess(response: IUserLoginFormResponse) {
    performLogin(response.userId, false);
    onSuccess();
  }

  function handleChange() {
    setNextDisabled(finishFormRef.current.api.current.getState().invalid);
  }

  return (
    <div className={bem(null, [{ login: true }])}>
      <div className={bem('login-title')}>{i18n.t(translationKeys.LoginTitle)}</div>
      <div className={bem('login-email-address')}>{formValues.emailAddress}</div>
      <Button onClick={openChangeEmailModal} className={bem('login-switch-address')} variant={'primary-link'}>
        {i18n.t(translationKeys.SwitchAddress)}
      </Button>

      <LoginForm
        onSuccess={handleSuccess}
        onAttemptStart={() => setFinishButtonSubmitting(true)}
        onAttemptEnd={() => setFinishButtonSubmitting(false)}
        onChange={handleChange}
        ref={finishFormRef}
        initialValues={{ email: formValues.emailAddress, rememberMe: true }}
        trackingId={CreateAnalyticsEvents.CREATE_STARTED}
        onForgotPassword={() => auth.resetPassword()}
        hideEmailField
        hideRememberMe
        hideButtons
      />
    </div>
  );
}

function ExternalLogin({
  provider,
  openChangeEmailModal,
  externalLoginReturnUrl,
}: {
  provider: ExternalLoginFormFilter;
  openChangeEmailModal: () => Promise<void>;
  externalLoginReturnUrl: string;
}) {
  const bem = bemBlock(blockName);
  const formValues = useCreateFlowStore(state => state.formValues);

  return (
    <div className={bem(null, [{ login: true }, { [provider]: true }])}>
      <div className={bem('login-title')}>{i18n.t(translationKeys.LoginTitle)}</div>
      <div className={bem('external-login-email-address')}>
        <HTMLParser
          rawHtml={i18n.t(translationKeys.ExternalLoginDescription, {
            emailAddress: formValues.emailAddress,
            provider,
          })}
        />
      </div>

      <Button onClick={openChangeEmailModal} className={bem('login-switch-address')} variant={'primary-link'}>
        {i18n.t(translationKeys.SwitchAddress)}
      </Button>

      <ExternalLoginForm filter={provider} returnUrl={externalLoginReturnUrl} />
    </div>
  );
}

const FacebookLogin = ({ openChangeEmailModal, externalLoginReturnUrl }) => (
  <ExternalLogin
    provider={'Facebook'}
    openChangeEmailModal={openChangeEmailModal}
    externalLoginReturnUrl={externalLoginReturnUrl}
  />
);

const GoogleLogin = ({ openChangeEmailModal, externalLoginReturnUrl }) => (
  <ExternalLogin
    provider={'Google'}
    openChangeEmailModal={openChangeEmailModal}
    externalLoginReturnUrl={externalLoginReturnUrl}
  />
);
